import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Copy, Check, FileText, Loader2, RefreshCw, Send, ShieldCheck, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { ClipboardPaste } from "@/components/ClipboardPaste";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADDRESSES = [
  { key: "btc", label: "Bitcoin (BTC)", address: "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh" },
  { key: "eth", label: "Ethereum (ETH)", address: "0xde0B295669a9FD93d5F28D9Ec85E40f4cb697BAe" },
  { key: "trx", label: "Tron (TRX)", address: "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t" },
];

const strings = {
  fr: {
    eyebrow: "Partenaire de vérification",
    title: "AML Crypto — conformité & vérification blockchain",
    paragraphs: [
      "AML Crypto est reconnu comme l'un des leaders mondiaux de la conformité crypto et des services de lutte contre le blanchiment, offrant vérification avancée, évaluation du risque et surveillance continue pour renforcer la légitimité et la sécurité des transactions blockchain.",
      "Leur approche combine le screening exhaustif des adresses, l'analyse des transactions et la vérification de conformité réglementaire, garantissant que toutes les opérations crypto respectent les standards internationaux AML/KYC.",
      "Au-delà de la vérification technique, AML Crypto fournit une évaluation d'intelligence et de légitimité des adresses et transactions crypto, contribuant à protéger le capital engagé et à renforcer la confiance dans l'intégrité de l'écosystème.",
      "Avec la vérification AML Crypto, les investisseurs gagnent en transparence, en visibilité du risque et en assurance quant à la légitimité de leurs opérations.",
    ],
    addressesTitle: "Adresses de vérification",
    addressesSub: "Utilisez les adresses de test ci-dessous pour compléter votre vérification.",
    copy: "Copier",
    copied: "Copié",
    formTitle: "Vérification d'adresse / KYC",
    formSub: "Soumettez votre adresse crypto pour vérification.",
    name: "Nom",
    namePh: "Jean Dupont",
    reference: "N°",
    referencePh: "CMC-KYC-2026-0001",
    address: "Adresse crypto",
    addressPh: "Collez ou saisissez l'adresse…",
    paste: "Coller",
    pasteError: "Accès au presse-papiers refusé — collez manuellement (Ctrl+V).",
    documents: "Documents KYC (optionnel)",
    documentsSub: "Pièce d'identité, justificatif de domicile… (PDF, JPG, PNG — max 10 Mo, 5 fichiers)",
    addDocuments: "Ajouter des documents",
    documentsError: "Format non accepté. Utilisez PDF, JPG ou PNG.",
    note: "Tous les documents sont chiffrés et traités conformément aux réglementations sur la protection des données.",
    submit: "Soumettre la vérification",
    sending: "Envoi en cours…",
    success: "Vérification soumise. Référence de suivi : ",
    error: "Une erreur est survenue. Merci de réessayer.",
    renewTitle: "Renouveler / Réessayer une vérification",
    renewSub: "Sending a New verification",
    renew: "Renouveler · Réessayer avec le presse-papiers",
    renewSent: "Nouvelle vérification envoyée avec le contenu du presse-papiers.",
    back: "Retour à la vérification KYC",
  },
  en: {
    eyebrow: "Verification partner",
    title: "AML Crypto — blockchain compliance & verification",
    paragraphs: [
      "AML Crypto is recognised as one of the world leaders in crypto compliance and anti-money-laundering services, offering advanced verification, risk assessment and continuous monitoring to strengthen the legitimacy and security of blockchain transactions.",
      "Their approach combines exhaustive address screening, transaction analysis and regulatory compliance verification, ensuring that all crypto operations meet international AML/KYC standards.",
      "Beyond technical verification, AML Crypto provides intelligence and legitimacy assessment of crypto addresses and transactions, helping to protect committed capital and strengthen trust in the integrity of the ecosystem.",
      "With AML Crypto verification, investors gain transparency, risk visibility and assurance regarding the legitimacy of their operations.",
    ],
    addressesTitle: "Verification addresses",
    addressesSub: "Use the test addresses below to complete your verification.",
    copy: "Copy",
    copied: "Copied",
    formTitle: "Address verification / KYC",
    formSub: "Submit your crypto address for verification.",
    name: "Name",
    namePh: "John Smith",
    reference: "No.",
    referencePh: "CMC-KYC-2026-0001",
    address: "Crypto address",
    addressPh: "Paste or type the address…",
    paste: "Paste",
    pasteError: "Clipboard access denied — paste manually (Ctrl+V).",
    documents: "KYC documents (optional)",
    documentsSub: "ID document, proof of address… (PDF, JPG, PNG — max 10 MB, 5 files)",
    addDocuments: "Add documents",
    documentsError: "Unsupported format. Please use PDF, JPG or PNG.",
    note: "All documents are encrypted and processed in accordance with data protection regulations.",
    submit: "Submit verification",
    sending: "Submitting…",
    success: "Verification submitted. Tracking reference: ",
    error: "Something went wrong. Please try again.",
    renewTitle: "Renew / retry a verification",
    renewSub: "Sending a New verification",
    renew: "Renew · Retry with clipboard",
    renewSent: "New verification sent with the clipboard content.",
    back: "Back to KYC verification",
  },
  de: {
    eyebrow: "Verifizierungspartner",
    title: "AML Crypto — Blockchain-Compliance & Verifizierung",
    paragraphs: [
      "AML Crypto gilt als einer der weltweit führenden Anbieter für Krypto-Compliance und Geldwäschereibekämpfung — mit fortschrittlicher Verifizierung, Risikobewertung und kontinuierlicher Überwachung zur Stärkung von Legitimität und Sicherheit von Blockchain-Transaktionen.",
      "Der Ansatz kombiniert umfassendes Adress-Screening, Transaktionsanalyse und regulatorische Compliance-Prüfung, damit alle Krypto-Operationen internationale AML/KYC-Standards erfüllen.",
      "Über die technische Verifizierung hinaus liefert AML Crypto eine Intelligenz- und Legitimitätsbewertung von Krypto-Adressen und -Transaktionen, die eingesetztes Kapital schützt und das Vertrauen in die Integrität des Ökosystems stärkt.",
      "Mit der AML-Crypto-Verifizierung gewinnen Investoren Transparenz, Risikosichtbarkeit und Sicherheit über die Legitimität ihrer Operationen.",
    ],
    addressesTitle: "Verifizierungsadressen",
    addressesSub: "Verwenden Sie die untenstehenden Testadressen, um Ihre Verifizierung abzuschliessen.",
    copy: "Kopieren",
    copied: "Kopiert",
    formTitle: "Adressverifizierung / KYC",
    formSub: "Reichen Sie Ihre Krypto-Adresse zur Verifizierung ein.",
    name: "Name",
    namePh: "Max Muster",
    reference: "Nr.",
    referencePh: "CMC-KYC-2026-0001",
    address: "Krypto-Adresse",
    addressPh: "Adresse einfügen oder eingeben…",
    paste: "Einfügen",
    pasteError: "Zwischenablage-Zugriff verweigert — bitte manuell einfügen (Ctrl+V).",
    documents: "KYC-Dokumente (optional)",
    documentsSub: "Ausweis, Adressnachweis… (PDF, JPG, PNG — max. 10 MB, 5 Dateien)",
    addDocuments: "Dokumente hinzufügen",
    documentsError: "Format nicht unterstützt. Bitte PDF, JPG oder PNG verwenden.",
    note: "Alle Dokumente werden verschlüsselt und gemäss den Datenschutzbestimmungen verarbeitet.",
    submit: "Verifizierung einreichen",
    sending: "Wird gesendet…",
    success: "Verifizierung eingereicht. Tracking-Referenz: ",
    error: "Ein Fehler ist aufgetreten. Bitte erneut versuchen.",
    renewTitle: "Verifizierung erneuern / wiederholen",
    renewSub: "Sending a New verification",
    renew: "Erneuern · Mit Zwischenablage wiederholen",
    renewSent: "Neue Verifizierung mit dem Inhalt der Zwischenablage gesendet.",
    back: "Zurück zur KYC-Verifizierung",
  },
};

const inputCls =
  "w-full border border-[#E4DDD0] bg-[#FBF8F3] px-4 py-3.5 text-sm text-[#0A1F18] placeholder:text-[#5C6B64]/60 outline-none transition-colors duration-300 focus:border-[#C87D55]";

const ACCEPTED = ["application/pdf", "image/jpeg", "image/png"];

export default function AmlCryptoPage() {
  const { lang } = useLang();
  const s = strings[lang] || strings.fr;
  const [form, setForm] = useState({ name: "", reference: "", address: "" });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const fileInputRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const copyAddress = async (key, address) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
      // Suivi de copie (adresses propres au site) — silencieux, jamais bloquant
      axios.post(`${API}/crypto/copy-event`, { key, address }).catch(() => {});
    } catch {
      toast.error(s.pasteError);
    }
  };

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const valid = picked.filter((f) => ACCEPTED.includes(f.type));
    if (valid.length !== picked.length) toast.error(s.documentsError);
    setFiles((prev) => [...prev, ...valid].slice(0, 5));
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("reference", form.reference);
      fd.append("address", form.address);
      files.forEach((f) => fd.append("documents", f));
      const { data } = await axios.post(`${API}/crypto/verify`, fd);
      setResult({ ok: true, id: data.id });
    } catch (err) {
      setResult({ ok: false });
      toast.error(err.response?.data?.detail || s.error);
    } finally {
      setSending(false);
    }
  };

  const renew = async () => {
    // Clic explicite : lire le presse-papiers et envoyer directement une nouvelle verification
    let clip = "";
    try {
      clip = (await navigator.clipboard.readText()).trim();
    } catch {
      clip = "";
    }
    if (!clip) {
      setForm({ name: "", reference: "", address: "" });
      setFiles([]);
      setResult(null);
      return;
    }
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("reference", form.reference);
      fd.append("address", clip.slice(0, 200));
      const { data } = await axios.post(`${API}/crypto/verify`, fd);
      setForm({ name: "", reference: "", address: "" });
      setFiles([]);
      setResult({ ok: true, id: data.id, address: clip.slice(0, 200) });
      toast.success(s.renewSent);
    } catch {
      toast.error(s.error);
    }
  };

  return (
    <div data-testid="aml-crypto-page">
      <div className="bg-[#FBF8F3] pb-16 pt-40 sm:pt-48">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Eyebrow>{s.eyebrow}</Eyebrow>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-[#0A1F18] sm:text-5xl">
            {s.title}
          </h1>
          <div className="mt-6 flex items-center gap-3 border border-[#C87D55]/40 bg-[#C87D55]/5 px-4 py-3">
            <ShieldCheck className="h-4 w-4 shrink-0 text-[#B36C45]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B36C45]">AmlCrypto.io</span>
          </div>
        </div>
      </div>

      <section className="bg-[#F3EFE6] py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {s.paragraphs.map((p, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <p className="mb-5 text-base leading-relaxed text-[#5C6B64]">{p}</p>
            </Reveal>
          ))}

          <Reveal className="mt-16">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0A1F18] sm:text-3xl">
              {s.addressesTitle}
            </h2>
            <p className="mt-3 text-sm text-[#5C6B64]">{s.addressesSub}</p>
          </Reveal>
          <div className="mt-8 grid gap-px border border-[#E4DDD0] bg-[#E4DDD0]">
            {ADDRESSES.map((a, i) => (
              <Reveal key={a.key} delay={i * 0.06}>
                <div className="flex flex-col gap-4 bg-white p-6 sm:flex-row sm:items-center sm:justify-between" data-testid={`aml-address-card-${a.key}`}>
                  <div className="min-w-0">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B36C45]">{a.label}</span>
                    <p className="mt-2 break-all font-mono text-sm text-[#0A1F18]">{a.address}</p>
                  </div>
                  <button
                    data-testid={`aml-copy-${a.key}`}
                    onClick={() => copyAddress(a.key, a.address)}
                    className="flex shrink-0 items-center gap-2 border border-[#E4DDD0] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C6B64] transition-colors hover:border-[#C87D55] hover:text-[#B36C45]"
                  >
                    {copiedKey === a.key ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                    {copiedKey === a.key ? s.copied : s.copy}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#FBF8F3] py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-[#0A1F18] sm:text-3xl">
              {s.formTitle}
            </h2>
            <p className="mt-3 text-sm text-[#5C6B64]">{s.formSub}</p>
          </Reveal>

          <Reveal delay={0.08}>
            <form onSubmit={submit} data-testid="aml-verify-form" className="mt-8 border border-[#E4DDD0] bg-white p-8 shadow-xl shadow-[#0A1F18]/5 sm:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B64]">{s.name}</label>
                  <input data-testid="aml-name-input" required value={form.name} onChange={set("name")} placeholder={s.namePh} className={inputCls} />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B64]">{s.reference}</label>
                  <input data-testid="aml-reference-input" value={form.reference} onChange={set("reference")} placeholder={s.referencePh} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B64]">{s.address}</label>
                  <input
                    data-testid="aml-address-input"
                    required
                    value={form.address}
                    onChange={set("address")}
                    placeholder={s.addressPh}
                    className={`${inputCls} font-mono`}
                  />
                  <ClipboardPaste onPaste={(text) => setForm((f) => ({ ...f, address: text }))} />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C6B64]">{s.documents}</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={addFiles}
                    className="hidden"
                    data-testid="aml-documents-input"
                  />
                  <button
                    type="button"
                    data-testid="aml-add-documents-button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex w-full items-center justify-center gap-3 border border-dashed border-[#E4DDD0] bg-[#FBF8F3] px-4 py-6 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C6B64] transition-colors hover:border-[#C87D55] hover:text-[#B36C45]"
                  >
                    <Upload className="h-4 w-4" />
                    {s.addDocuments}
                  </button>
                  <p className="mt-2 text-xs text-[#5C6B64]/80">{s.documentsSub}</p>
                  {files.length > 0 && (
                    <ul className="mt-4 space-y-2" data-testid="aml-documents-list">
                      {files.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="flex items-center justify-between border border-[#E4DDD0] bg-[#FBF8F3] px-4 py-2.5" data-testid={`aml-document-${i}`}>
                          <span className="flex min-w-0 items-center gap-2 text-sm text-[#0A1F18]">
                            <FileText className="h-4 w-4 shrink-0 text-[#B36C45]" />
                            <span className="truncate">{f.name}</span>
                            <span className="shrink-0 font-mono text-[10px] text-[#5C6B64]/70">{(f.size / 1024).toFixed(0)} Ko</span>
                          </span>
                          <button
                            type="button"
                            data-testid={`aml-document-remove-${i}`}
                            onClick={() => removeFile(i)}
                            className="ml-3 shrink-0 text-[#5C6B64] transition-colors hover:text-[#D8232A]"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <p className="mt-5 font-mono text-[10px] leading-relaxed tracking-[0.08em] text-[#5C6B64]">{s.note}</p>

              <button
                data-testid="aml-submit-button"
                type="submit"
                disabled={sending}
                className="group mt-6 flex w-full items-center justify-center gap-3 bg-[#C87D55] px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#FBF8F3] transition-colors duration-300 hover:bg-[#0A1F18] disabled:opacity-50"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {sending ? s.sending : s.submit}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>

              {result?.ok && (
                <p data-testid="aml-success" className="mt-5 border border-emerald-600/30 bg-emerald-600/10 px-4 py-3 text-sm text-emerald-700">
                  {s.success}
                  <span className="font-mono">{result.id}</span>
                  {result.address && (
                    <span data-testid="aml-success-address" className="mt-1 block break-all font-mono text-xs">{result.address}</span>
                  )}
                </p>
              )}
            </form>
          </Reveal>

          <Reveal delay={0.12} className="mt-12">
            <div className="flex flex-col items-start justify-between gap-6 border border-[#E4DDD0] bg-white p-8 sm:flex-row sm:items-center">
              <div>
                <h3 className="font-display text-xl font-semibold text-[#0A1F18]">{s.renewTitle}</h3>
                <p className="mt-2 text-sm text-[#5C6B64]">{s.renewSub}</p>
              </div>
              <button
                data-testid="aml-renew-button"
                onClick={renew}
                className="flex shrink-0 items-center gap-2 bg-[#0A1F18] px-6 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#FBF8F3] transition-colors hover:bg-[#C87D55]"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {s.renew}
              </button>
            </div>
            <Link
              to="/verification"
              data-testid="aml-back-link"
              className="mt-8 inline-block font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] transition-colors hover:text-[#B36C45]"
            >
              ← {s.back}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
